import { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
} from 'recharts';
import { menuKaynagi } from './ekranKaynaklari';
import { useSaSorgu } from './useSatisVerisi';
import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { demoTheme } from '../../theme';
import type { ReportConfig } from '../../types/panelConfig';
import type { Donem } from './donem';
import { kanalRengi, kanalSirala } from './kanalRenk';
import { kanalSuz, subeSuz } from './subeSuzgeci';
import { kanalGrubu } from './kanal';
import { BlokIskeleti } from './BlokIskeleti';

interface KanalSatiri {
  tarih: string;
  lokasyon: string;
  kanal_grup: string;
  kanal: string;
  fis_sayisi: number;
  net_ciro: number;
}

interface KanalOzet {
  kanalGrup: string;
  ciro: number;
  fis: number;
  sepet: number | null;
}

interface LokasyonOzet {
  lokasyon: string;
  magazaCiro: number;
  magazaFis: number;
  magazaSepet: number | null;
  digerKanallar: KanalOzet[];
}

interface Props {
  reports: ReportConfig[];
  donem: Donem;
  /** null = tum subeler. Secildiginde blok TEK sube gosterir. */
  sube?: string | null;
  /** G2: sube paylari secili kanala gore suzulur (spec §3). */
  kanal?: string | null;
}

function toplamCiro(l: LokasyonOzet): number {
  return l.magazaCiro + l.digerKanallar.reduce((t, k) => t + k.ciro, 0);
}

/**
 * ONCEDEN (fix1'e kadar): 040 lokasyon dondurmuyordu, bu yuzden "es-magaza"
 * rakami 013'un ham cirosundan sirket capinda bir online toplami cikararak
 * TAHMIN EDILIYORDU - "hangi sube online tasiyor" da ayrica config'ten
 * okunan bir sabitti. O sinirlama artik YOK: 040 "Satış Kanalı Özeti" ARTIK
 * `lokasyon` kolonu tasiyor (canlida dogrulandi, kanal_grup toplamlari
 * degismedi). Dolayisiyla mağaza kanalı cirosu her lokasyon icin DOGRUDAN
 * OLCULEBILIYOR - cikarma, sirket capinda toplam, config'ten okunan "hangi
 * sube online tasiyor" sabiti TAMAMEN KALKTI. Olculmus ornek (Gizova, 90
 * gun): ATAKENT mağaza 17.040.147 (ayrica 134.493 kurumsal + 55 online +
 * -307 pazaryeri iade) · Merkez mağaza 4.556.161 (ayrica 10.589.885
 * pazaryeri + 3.087.883 kurumsal + 239.711 online). Eski "ATAKENT yalniz
 * magaza satiyor" varsayimi YANLIS cikti - ATAKENT'in de kurumsal/online
 * kanali var, sadece kucuk. Eski "Merkez magaza = 7.897.949" rakami da
 * yanlisti - o rakam kurumsalin magazaya katilmasindan kaynaklaniyordu.
 *
 * 013 (sube donemsel) artik KULLANILMIYOR: 040 ayni "magaza kanali cirosu"nu
 * dogrudan verirken, 013'un `degisim_pct`'i TUM kanallarin karisimidir (013
 * kanala gore ayrisamaz) - yani yanindaki rakamdan FARKLI bir tabana sahip
 * bir degisim yuzdesi gostermis olurduk. Boyle bir uyumsuzluk sessiz bir
 * yanlislik uretir; o yuzden donem-uzeri degisim burada hic gosterilmiyor.
 */
/**
 * Yigin grafiginin sutun kumesi: veride GERCEKTEN gecen kanal gruplari,
 * sabit bir liste DEGIL. Beklenmedik bir grup gelirse de cizilir (sona
 * alinir, bkz. `kanalSirala`) - sessizce dusurulmez, cunku dusurulen kanal
 * yigini toplamdan KUCUK gosterir ve grafik yine yalan soylerdi.
 */
export function grafikKanallari(lokasyonlar: LokasyonOzet[]): string[] {
  const kume = new Set<string>();
  for (const l of lokasyonlar) {
    if (l.magazaFis !== 0 || l.magazaCiro !== 0) kume.add('Mağaza');
    for (const k of l.digerKanallar) kume.add(k.kanalGrup);
  }
  return kanalSirala(Array.from(kume));
}

/**
 * Gosterge. recharts'in kendi <Legend>'i KULLANILMIYOR:
 *
 * 🔴 CANLIDA OLCULDU (2026-08-21): recharts v3 gostergeyi kendi sirasiyla
 * (alfabetik: Kurumsal, Mağaza, Online, Pazaryeri) basiyordu; yigin ise
 * Mağaza, Pazaryeri, Kurumsal, Online sirasindaydi - okuyucu gostergedeki
 * sirayla cubuktaki dilim sirasini eslestiremiyordu. `payload` prop'u ile
 * duzeltmek MUMKUN DEGIL: recharts v3 onu Legend'in acik tipinden Omit
 * ediyor (tsc yakaladi; recharts'i taklit eden test o yuzden YANLIS YESIL
 * vermisti). Kendi seridimizi basmak hem sirayi hem rengi yiginla AYNI
 * kaynaktan (kanallar + kanalRengi) alir ve jsdom'da OLCULEBILIR - grafigin
 * ici olculemezdi.
 */
export function Gosterge({ kanallar }: { kanallar: string[] }) {
  return (
    <div data-testid="sube-gosterge" className="flex flex-wrap items-center justify-center gap-4">
      {kanallar.map((k) => (
        <span key={k} data-testid={`gosterge-${k}`} className="flex items-center gap-1.5 text-[12px] text-ink-500">
          <span
            className="w-2.5 h-2.5 rounded-[2px] shrink-0"
            style={{ background: kanalRengi(k) }}
          />
          {k}
        </span>
      ))}
    </div>
  );
}

/**
 * Yigin grafiginin satirlari: lokasyon basina her kanal ayri bir alan.
 * Eksik kanal 0 yazilir (undefined birakmak recharts'ta yigini kaydirir).
 */
export function grafikSatirlari(
  lokasyonlar: LokasyonOzet[],
  kanallar: string[],
): Array<Record<string, string | number>> {
  return lokasyonlar.map((l) => {
    const satir: Record<string, string | number> = { lokasyon: l.lokasyon };
    for (const k of kanallar) satir[k] = 0;
    if (kanallar.includes('Mağaza')) satir['Mağaza'] = l.magazaCiro;
    for (const k of l.digerKanallar) satir[k.kanalGrup] = k.ciro;
    return satir;
  });
}

export function SubeBlok({ reports, donem, sube = null, kanal = null }: Props) {
  // Sonek 40 + ad kontrolu. Anahtar paylasimi icin sube/kanal sorguya GIRMEZ; istemcide suzulur.
  const kaynak = menuKaynagi(reports, 40, 'Satış Kanalı Özeti');
  const kanalQ = useSaSorgu<KanalSatiri>(kaynak, { donem, sube: null, kanal: null });

  const lokasyonlar = useMemo<LokasyonOzet[]>(() => {
    const rows = kanalSuz(subeSuz(kanalQ.data?.data ?? [], sube), kanal);

    // lokasyon -> kanal_grup -> {ciro, fis} - `tarih` boyunca toplanir.
    // Sabit bir lokasyon/kanal_grup kumesi VARSAYILMAZ (`Bilinmeyen` lokasyon
    // ya da beklenmedik bir kanal_grup gelirse de dogru gruplanir).
    const map = new Map<string, Map<string, { ciro: number; fis: number }>>();
    for (const r of rows) {
      const lokasyon = String(r.lokasyon);
      const kanalGrup = String(r.kanal_grup);
      if (!map.has(lokasyon)) map.set(lokasyon, new Map());
      const grupMap = map.get(lokasyon)!;
      if (!grupMap.has(kanalGrup)) grupMap.set(kanalGrup, { ciro: 0, fis: 0 });
      const v = grupMap.get(kanalGrup)!;
      v.ciro += Number(r.net_ciro ?? 0);
      v.fis += Number(r.fis_sayisi ?? 0);
    }

    return Array.from(map.entries())
      .map(([lokasyon, grupMap]) => {
        const magaza = grupMap.get('Mağaza') ?? { ciro: 0, fis: 0 };
        const digerKanallar: KanalOzet[] = Array.from(grupMap.entries())
          .filter(([kanalGrup]) => kanalGrup !== 'Mağaza')
          .map(([kanalGrup, v]) => ({
            kanalGrup,
            ciro: v.ciro,
            fis: v.fis,
            // Sepet ortalamasi: fis=0 iken SIFIRA BOLME yapilmaz (ör. ATAKENT
            // Pazaryeri: 0 fis, -307 ciro - iade, pencere icinde satis yok).
            sepet: v.fis > 0 ? v.ciro / v.fis : null,
          }))
          .sort((a, b) => b.ciro - a.ciro);

        return {
          lokasyon,
          magazaCiro: magaza.ciro,
          magazaFis: magaza.fis,
          magazaSepet: magaza.fis > 0 ? magaza.ciro / magaza.fis : null,
          digerKanallar,
        };
      })
      // Yigin grafigi TUM kanallari gosterdigine gore siralama da TOPLAM
      // ciroya gore olmali; magaza cirosuna gore siralamak grafikte kisa
      // gorunen subeyi uste koyabilirdi (Merkez: magaza 4,5M ama toplam 18,5M).
      .sort((a, b) => toplamCiro(b) - toplamCiro(a));
  }, [kanalQ.data, sube, kanal]);

  const kanallar = useMemo(() => grafikKanallari(lokasyonlar), [lokasyonlar]);

  // Magaza disi kanal seciliyse "· Mağaza" satiri ₺0,00 basardi; gizlenir.
  const magazaGoster = kanal === null || kanalGrubu(kanal) === 'Mağaza';

  if (!kaynak) return null; // rapor yok/ad tutmuyor -> sorgu bile acilmaz
  // Bos durum kurali: suzgec YOKKEN 2'den az lokasyon varsa kiyaslanacak
  // bir sey yoktur, blok gizlenir. Sube SECILIYSE tek lokasyon beklenen
  // sonuctur - o zaman blok kanal kirilimini gostermeye devam eder.
  // Sorgu SURERKEN blok `null` DONMEZ: yerini tutan bir iskelet doner.
  // Asagidaki bos-durum kurallari yukleme sirasinda da dogru olurdu
  // (veri henuz yok), yani iskelet olmadan blok gec gelip altindakileri
  // asagi iterdi -- bkz. BlokIskeleti'ndeki olcum.
  if (kanalQ.isLoading)
    return (
      <BlokIskeleti
        testid="blok-sube-iskelet"
        baslik="Şube karşılaştırması"
        yukseklik={300}
        satir={5}
      />
    );
  if (sube === null && lokasyonlar.length < 2) return null;
  if (lokasyonlar.length === 0) return null;

  return (
    <div data-testid="blok-sube">
      <SectionCard
        title="Şube karşılaştırması"
        subtitle={
          kanal !== null
            ? `${kanal} kanalı — şube payları`
            : sube
              ? `${sube} — kanal kırılımı`
              : 'Şubenin TÜM kanalları — yığın; renkler kanal bloğuyla aynı'
        }
      >
        <div className="flex flex-col gap-4">
          <div style={{ width: '100%', height: 40 + lokasyonlar.length * 46 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={grafikSatirlari(lokasyonlar, kanallar)}
                layout="vertical"
                margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="2 4" stroke={demoTheme.border} horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(v) => fmtNum(Number(v))}
                  tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                  axisLine={{ stroke: demoTheme.border }}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="lokasyon"
                  tick={{ fontSize: 12, fill: demoTheme.textPrimary }}
                  axisLine={false}
                  tickLine={false}
                  width={88}
                />
                <ChartTooltip formatter={(v, ad) => [fmtTRY(Number(v)), String(ad)]} />
                {kanallar.map((k, i) => (
                  <Bar
                    key={k}
                    dataKey={k}
                    stackId="ciro"
                    fill={kanalRengi(k)}
                    // Yuvarlatma YALNIZ yiginin son parcasinda; her parcaya
                    // verilirse aradaki dilimler birbirinden kopuk gorunur.
                    radius={i === kanallar.length - 1 ? [0, 2, 2, 0] : undefined}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <Gosterge kanallar={kanallar} />

          <div className="flex flex-col gap-3">
            {lokasyonlar.map((l) => (
              <div key={l.lokasyon} className="flex flex-col gap-1">
                {magazaGoster && (
                  <div
                    data-testid={`sube-${l.lokasyon}`}
                    className="flex items-center justify-between gap-3 py-1"
                  >
                    <span className="text-[13px] font-medium text-ink-900 truncate">
                      {l.lokasyon} · Mağaza
                    </span>
                    <span className="text-[13px] text-ink-900 tnum">
                      {fmtTRY(l.magazaCiro)}
                      {l.magazaSepet !== null && (
                        <span className="text-ink-400 font-normal"> · sepet {fmtTRY(l.magazaSepet)}</span>
                      )}
                    </span>
                  </div>
                )}
                {/* Diger kanallar: neden toplamlarin farkli oldugunu ACIKCA
                    gostermek icin ayri satirlarda - bare bir "magaza" rakami
                    baglamsiz sunulmaz. */}
                {l.digerKanallar.length > 0 && (
                  <div className={`${magazaGoster ? 'pl-4' : ''} flex flex-col gap-0.5`}>
                    {l.digerKanallar.map((k) => (
                      <div
                        key={k.kanalGrup}
                        data-testid={`sube-${l.lokasyon}-kanal-${k.kanalGrup}`}
                        className="flex items-center justify-between gap-3 text-[12px] text-ink-600"
                      >
                        <span className="truncate">{k.kanalGrup}</span>
                        <span className="tnum">
                          {fmtTRY(k.ciro)}
                          {k.sepet !== null && <span className="text-ink-400"> · sepet {fmtTRY(k.sepet)}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
